import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useFetch } from 'use-http'

import { Box, Button, Card, Heading, Stack, Text, TextField } from '@myxplor/stardust'

import sortObjectsBy from '~/src/utils/sortObjectsBy'

import LoadingLoop from '~/src/components/LoadingLoop'

import AccountModal from './AccountModal'

const LINK_STYLE = {
  color: 'inherit',
  textDecoration: 'none',
  width: '100%',
}

const AccountList = () => {
  const [searchText, setSearchText] = useState('')

  const accountRequest = useFetch('/api/v1/accounts', {}, [])
  const vendorRequest = useFetch('/api/v1/vendors', {}, [])

  const [createdAccounts, setCreatedAccounts] = useState<Papi.Account[]>([])
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedAccount, setSelectedAccount] = useState<Nullable<Papi.Account>>(null)

  const vendors = useMemo(() => {
    return sortObjectsBy(vendorRequest.data?.data || [], 'name') as Papi.Vendor[]
  }, [vendorRequest.data])

  const vendorLookup = useMemo(() => {
    return vendors.reduce((acc, vendor) => {
      return { ...acc, [vendor.id]: vendor.name }
    }, {}) as Record<number, string>
  }, [vendors])

  const accounts = useMemo(() => {
    const allAccounts = [...createdAccounts, ...(accountRequest.data?.data || [])]
    const lowerSearchText = searchText.toLowerCase()
    const filteredAccounts = allAccounts.filter((account) => {
      return (
        account.name.toLowerCase().includes(lowerSearchText) ||
        account.tenant_id?.toLowerCase().includes(lowerSearchText) ||
        account.username.toLowerCase().includes(lowerSearchText) ||
        vendorLookup[account.vendor_id].toLowerCase().includes(lowerSearchText)
      )
    })
    return sortObjectsBy(filteredAccounts, 'name') as Papi.Account[]
  }, [accountRequest.data, createdAccounts, searchText, vendorLookup])

  const onNewAccount = () => {
    setSelectedAccount(null)
    setModalOpen(true)
  }

  const onSave = async (account: Papi.Account) => {
    setModalOpen(false)
    setCreatedAccounts((accounts) => [...accounts, account])
  }

  if (accountRequest.loading || vendorRequest.loading) {
    return <LoadingLoop />
  }

  if (accountRequest.error || vendorRequest.error) {
    return <div>Error...</div>
  }

  return (
    <Stack space="small">
      <Box alignItems="center" display="flex" justifyContent="space-between" width={1}>
        <Heading level={4}>Accounts</Heading>
        <Button onClick={onNewAccount}>Add account</Button>
      </Box>
      <TextField
        label="Search"
        name="search"
        value={searchText}
        onChange={(value: string) => setSearchText(value)}
      />
      <Card>
        <Stack space="none">
          {accounts.map((account, index) => (
            <Link key={account.id} style={LINK_STYLE} to={`/accounts/${account.id}`}>
              <Box
                alignItems="center"
                borderTop={index === 0 ? undefined : '1px solid'}
                borderColor={index === 0 ? undefined : 'lowerEmphasis'}
                cursor="pointer"
                display="flex"
                hoverStyle={({ theme }) => ({ backgroundColor: theme.colors.lowestEmphasis })}
                justifyContent="space-between"
                paddingY="xxsmall"
                transition="background-color .2s"
                width={1}>
                <Box flexGrow={1} padding="small">
                  <Text>{account.name}</Text>
                </Box>
                <Box display="flex">
                  <Box display="flex" flexDirection="column" paddingX="xsmall">
                    <Text align="right" prefab="overline">
                      BMS
                    </Text>
                    <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                      {vendorLookup[account.vendor_id]}
                    </Text>
                  </Box>
                  {account.tenant_id && (
                    <Box display="flex" flexDirection="column" paddingX="xsmall">
                      <Text align="right" prefab="overline">
                        Tenant
                      </Text>
                      <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                        {account.tenant_id}
                      </Text>
                    </Box>
                  )}
                  <Box display="flex" flexDirection="column" paddingX="xsmall">
                    <Text align="right" prefab="overline">
                      Username
                    </Text>
                    <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                      {account.username}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Link>
          ))}
        </Stack>
      </Card>
      <AccountModal
        account={selectedAccount}
        isOpen={isModalOpen}
        vendors={vendors}
        onCancel={() => setModalOpen(false)}
        onSave={onSave}
      />
    </Stack>
  )
}

export default React.memo(AccountList)
